import { BookOpenCheck, FolderTree, ImageIcon, PackageOpen, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const publishingChecklist = [
  'Complete Basic Info with product name, developer, category, tags, and pricing.',
  'Write short and long storefront descriptions in the Description tab.',
  'Upload hero and thumbnail assets to the marketplace bucket from Assets.',
  'Set featured and installer options in Settings when needed.',
  'Decide whether the upload is a single-file HTML bundle or an .mpak package.',
  'Set package_type and package_path so the product points to the correct uploaded file.',
  'For .mpak uploads, include a root manifest.json with name, version, type, loader, and entry.',
  'Review team roles so owners and admins can maintain the listing safely.',
];

const conventions = [
  {
    title: 'Assets',
    description: 'Hero and thumbnail images are stored in the public marketplace bucket using SKU-based paths.',
    icon: ImageIcon,
    value: 'products/<sku>/hero.<ext> and products/<sku>/thumbnail.<ext>',
  },
  {
    title: 'Packages',
    description: 'Use package_type and package_path to distinguish HTML bundles from .mpak installs.',
    icon: PackageOpen,
    value: 'bundle = single HTML file · package = .mpak ZIP with root manifest.json',
  },
  {
    title: 'Store metadata',
    description: 'Ratings are read-only summaries; storefront metadata is edited directly from the app detail tabs.',
    icon: FolderTree,
    value: 'Basic Info, Description, Settings, Publish, Team',
  },
];

export default function DocsPanel() {
  return (
    <div className="space-y-6 p-6 lg:p-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Documentation</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Embedded publishing guidance for shipping HTML bundles and .mpak packages to Maple Market without leaving MapleOS.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpenCheck className="h-4 w-4 text-primary" />
            Publishing checklist
          </CardTitle>
          <CardDescription>Use this checklist before marking a product ready for launch.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          {publishingChecklist.map((item, index) => (
            <div key={item} className="flex items-start gap-3 rounded-xl border border-border p-4">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                {index + 1}
              </div>
              <p>{item}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-3">
        {conventions.map((item) => (
          <Card key={item.title}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <item.icon className="h-4 w-4 text-primary" />
                {item.title}
              </CardTitle>
              <CardDescription>{item.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-foreground">
                {item.value}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Workflow reminder
          </CardTitle>
          <CardDescription>The embedded admin keeps product, team, assets, and release metadata together in one place.</CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Open Applications to edit a listing, make the package type clear to your team, and ensure the uploaded file matches the runtime metadata before release.
        </CardContent>
      </Card>
    </div>
  );
}